import { useState } from "react";

import { getApiErrorMessage, isHttpError } from "../services/apiService";
import { searchAddressByPostcode, getMeterDetailsByAddress } from "../services/electralink.service";
import { MeterDetails, Address } from "../types/components.types";
// import the functions below to retrieve address details based on the user's postcode input and fetch meter details when an address is selected.

const useAddressSearch = () => {
  // Set up state variables for the Address search tab to track input, validation, loading, errors, and fetched meter details.
  const [postcodeInput, setPostcodeInput] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [addressResults, setAddressResults] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const [meterDetails, setMeterDetails] = useState<MeterDetails | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Define an async function for address searching. Since API requests may take time, async/await is used for better API handling.
  const handleSearch = async () => {
    // Clear previous results at the beginning of any new search.
    setAddressResults([]);
    setSelectedAddress("");
    setMeterDetails(null);

    // Trim any white-spaces from the postcode input, then check if there has been a valid input.
    // Exit early if there has been no postcode input.
    if (!postcodeInput.trim()) {
      setError("Please enter a postcode");
      return;
    }

    // Clear any previous error messages before making a new API call.
    setError(null);
    setIsLoading(true);

    try {
      // Call searchAddressByPostcode with the user's input and update addressResults. This is to store and retrieve the addresses matching a postcode.
      const res = await searchAddressByPostcode(postcodeInput);

      if (res.success) {
        setAddressResults(res.addresses);

        // Handle errors if no addresses are returned.
        if (res.addresses.length === 0) {
          setError("No addresses found for this postcode.");
        }
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError("An error occurred while searching. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Define an async function to handle address selection and fetch meter details for the selected address.
  const handleAddressSelection = async (selectedAddress: string) => {
    setSelectedAddress(selectedAddress);
    setIsLoading(true);
    setError(null);

    // Find the selected address object in the addressResults array by matching the full address.
    const address = addressResults.find((addr) => addr.address === selectedAddress);
    // Return and display an error message if the selected address is not found.
    if (!address) {
      setError("No meter details were found for the selected address.");
      setMeterDetails(null);
      setIsLoading(false);
      return;
    }

    try {
      // Call getMeterDetailsByAddress() to fetch meter details and update the state. This is to display the meter details on the UI.
      const details = await getMeterDetailsByAddress(address);

      if (details.error) {
        setError(details.error);
        setMeterDetails(null);
      } else {
        setMeterDetails(details);
      }
    } catch (err) {
      // Handle errors by displaying a message and resetting meter details if the API call fails.
      // Prefer a friendly message derived from the API's response (e.g. a 404 "no meter found")
      // over the generic fallback.
      setError(
        isHttpError(err)
          ? getApiErrorMessage(err, "No meter details were found for the selected address.")
          : "An error occurred while fetching meter details.",
      );
      setMeterDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    postcodeInput,
    setPostcodeInput,
    error,
    setError,
    addressResults,
    setAddressResults,
    selectedAddress,
    setSelectedAddress,
    meterDetails,
    setMeterDetails,
    isLoading,
    setIsLoading,
    handleSearch,
    handleAddressSelection,
  };
};

export default useAddressSearch;
